 
import React, { useEffect, useState } from "react";
import { SFDataGrid, getLongDate, SFLoadingBar } from "@sf-core";

import SFProficiencyLevelRenderer from "../../components/renderers/proficiencyLevelRenderer";
import SFRankRenderer from "../../components/renderers/rankRenderer";
import SFColumnRankRenderer from "../../components/renderers/columnRankRenderer";
import SFToggleMasterDetailCell from "../../components/renderers/toggleMasterDetailCell";
import SFSkillTypesRenderer from "../../components/renderers/skillTypesRenderer";
import ExpandAllHeader from "../../components/renderers/expandAllHeader";

import { GRID_NAMES } from "../../../../constants/gridNames";
import { getProficiencyLevelLabel } from "../../../../constants/proficiencyLevels";
import { useTranslation } from "react-i18next";
import ProgressDialogs from "../../components/progressDialogs";
import { PROGRESS_TYPES } from "../../../../constants/progressTypes";

const MySkills = ({ skills, clearFilters }) => {
  const { t } = useTranslation();
  const [rankedSkills, setRankedSkills] = useState([]);
  const [dialog, setDialog] = useState({ skillId: null, open: false });

  useEffect(() => {
    const newSkills = skills.sort((a, b) => {
      const proficiency = b.proficiencyLevel - a.proficiencyLevel;
      return proficiency || a.skillName.localeCompare(b.skillName);
    });

    setRankedSkills(
      newSkills.map((skill, index) => ({
        ...skill,
        rank: index + 1,
        isBelowTarget: skill.proficiencyLevel < skill.proficiencyLevelTarget,
      }))
    );

    return () => {
      clearFilters();
    };
  }, [skills]);

  const mySkillsColumnDefs = [
    {
      field: "id",
      hide: true,
    },
    {
      headerName: "",
      minWidth: 38,
      maxWidth: 38,
      cellClass: "ag-small-cell",
      headerClass: "ag-small-cell ag-center-aligned-header",
      customHeader: ExpandAllHeader,
      cellStyle: {
        display: "flex",
      },
      cellRenderer: "SFToggleMasterDetailCell",
    },
    {
      field: "rank",
      headerName: "",
      cellRenderer: "SFRankRenderer",
      minWidth: 38,
      maxWidth: 38,
      cellClass: "ag-small-cell",
      headerClass: "ag-small-cell ag-center-aligned-header",
      sortable: true,
      sortingOrder: ["asc", "desc"],
      sort: "asc",
    },
    {
      field: "skillName",
      headerName: t("dashboardScreen.mySkillsGridColumns.skillName"),
      //cellClass: "proficiency-bar",
      cellStyle: {
        display: "flex",
      },
      cellRenderer: "SFSkillTypesRenderer",
      cellRendererParams: {
        onClick: (data) => {
          setDialog({
            skillId: data.id,
            open: true,
            type: data.isSkillGoal
              ? PROGRESS_TYPES.SKILL_GOAL
              : PROGRESS_TYPES.JOB_SKILL,
          });
        },
      },
      // wrapText: true,
      autoHeight: true,
    },
    {
      field: "proficiencyLevel",
      headerName: t("dashboardScreen.mySkillsGridColumns.proficiencyLevel"),
      cellRenderer: "SFProficiencyLevelRenderer",
      cellClass: "proficiency-bar",
      cellStyle: {
        display: "flex",
      },
      minWidth: 170,
      maxWidth: 170,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: "isBelowTarget",
      hide: true,
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["true"],
      },
    },
    {
      field: "lastProgressedDate",
      headerName: t("dashboardScreen.mySkillsGridColumns.lastProgressedDate"),
      valueFormatter: (p) => getLongDate(p.value),
      minWidth: 130,
      maxWidth: 130,
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          const cellDate = new Date(cellValue);
          if (cellDate < filterLocalDateAtMidnight) {
            return -1;
          } else if (cellDate > filterLocalDateAtMidnight) {
            return 1;
          } else {
            return 0;
          }
        },
        defaultOption: "inRange",
        inRangeInclusive: true,
      },
    },
    {
      field: "totalMicroSkills",
      headerName: t("dashboardScreen.mySkillsGridColumns.microSkills"),
      cellRenderer: "SFRankRenderer",
      minWidth: 100,
      maxWidth: 100,
      cellClass: "ag-center-aligned-cell",
    },
  ];

  if (skills.length === 0 || rankedSkills.length === 0) return <SFLoadingBar />;

  return (
    <>
      {dialog?.open && (
        <ProgressDialogs dialog={dialog} setDialog={setDialog} />
      )}
      <SFDataGrid
        hasFilterChips
        getSearchChipDetail={(item) => {
          if (item.label == "lastProgressedDate") {
            return {
              value: item.value,
              field: item.label,
              label: t("dashboardScreen.lastProgressedDateFilter"),
            };
          } else if (item.label == "isBelowTarget") {
            return {
              value: item.value,
              field: item.label,
              label: t("dashboardScreen.isBelowTargetFilter"),
            };
          } else
            return {
              value: item.value,
              field: item.label,
              label: t(
                getProficiencyLevelLabel(
                  item.value,
                  "lxpComponents.proficiencyLevel.searchChips"
                )
              ),
            };
        }}
        onRemoveSearchChip={(item) => {}}
        rowModelType="clientSide"
        gridName={GRID_NAMES.DASHBOARD_MY_SKILLS}
        customComponents={{
          SFProficiencyLevelRenderer,
          SFRankRenderer,
          SFColumnRankRenderer,
          SFToggleMasterDetailCell,
          SFSkillTypesRenderer,
        }}
        transparent
        noCardBorder
        disableBasicToolBar
        columnDefs={mySkillsColumnDefs}
        rowData={rankedSkills}
      />
    </>
  );
};
export default MySkills;
